import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAwareClick } from '../hooks/useScrollAwareClick';
import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  Tag,
  message,
  Row,
  Col,
  Avatar,
  Skeleton,
  Dropdown,
  Badge,
  Empty,
  Pagination,
  Flex,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  PhoneOutlined,
  MailOutlined,
  CarOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { Customer } from '../types';
import { customerApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Search } = Input;
const { Text, Title } = Typography;

// Helper function to get role-based path prefix
const getRolePrefix = (userRole?: string): string => {
  return userRole === 'customer' ? '/portal' :
         userRole === 'technician' ? '/technician' :
         userRole === 'advisor' ? '/advisor' :
         userRole === 'owner' ? '/owner' : '/admin';
};

interface CustomerCardProps {
  customer: Customer;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onCall: (customer: Customer) => void;
  onEmail: (customer: Customer) => void;
  rolePrefix: string;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  onEdit,
  onDelete,
  onCall,
  onEmail,
  rolePrefix,
}) => {
  const navigate = useNavigate();
  const { getClickHandler, touchHandlers } = useScrollAwareClick();

  const getInitials = (firstName: string, lastName: string, companyName?: string) => {
    if (companyName) {
      return companyName.slice(0, 2).toUpperCase();
    }
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getDisplayName = () => {
    return customer.company_name || `${customer.first_name} ${customer.last_name}`;
  };

  const actionMenuItems = [
    {
      key: 'edit',
      label: 'Edit Customer',
      icon: <EditOutlined />,
      onClick: () => onEdit(customer),
    },
    {
      key: 'call',
      label: 'Call Customer',
      icon: <PhoneOutlined />,
      onClick: () => onCall(customer),
    },
    {
      key: 'email',
      label: 'Send Email',
      icon: <MailOutlined />,
      onClick: () => onEmail(customer),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'delete',
      label: 'Delete Customer',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => onDelete(customer),
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 20,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        height: '100%',
        width: '100%'
      }}
      styles={{ body: { padding: '20px' } }}
      onClick={getClickHandler(() => navigate(`${rolePrefix}/customers/${customer.id}`))}
      {...touchHandlers}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', minWidth: 0 }}>
        {/* Avatar */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            size={64}
            style={{
              fontWeight: 'bold',
              background: customer.company_name
                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: '3px solid white',
              boxShadow: customer.company_name
                ? '0 8px 16px rgba(99, 102, 241, 0.3)'
                : '0 8px 16px rgba(59, 130, 246, 0.3)'
            }}
            icon={!customer.company_name ? <UserOutlined style={{ fontSize: '20px' }} /> : undefined}
          >
            {getInitials(customer.first_name, customer.last_name, customer.company_name)}
          </Avatar>
          <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '20px', height: '20px', backgroundColor: '#10b981', border: '2px solid white', borderRadius: '50%' }}></div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                {getDisplayName()}
              </h3>
              {customer.company_name && (
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: '500' }}>
                  {customer.first_name} {customer.last_name}
                </p>
              )}
            </div>
            <Dropdown
              menu={{ items: actionMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                style={{ color: '#6b7280' }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>

          {/* Contact Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: '#10b981', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PhoneOutlined style={{ color: 'white', fontSize: '14px' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: '#059669', fontWeight: '500' }}>Phone</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{customer.phone}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #dbeafe' }}>
              <div style={{ width: '32px', height: '32px', backgroundColor: '#3b82f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MailOutlined style={{ color: 'white', fontSize: '14px' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: '500' }}>Email</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email}</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
            <Space>
              <Badge
                count={3}
                size="small"
                style={{ cursor: 'pointer', backgroundColor: '#3b82f6' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${rolePrefix}/customers/${customer.id}`);
                }}
              >
                <Button
                  type="link"
                  icon={<CarOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${rolePrefix}/customers/${customer.id}`);
                  }}
                >
                  Vehicles
                </Button>
              </Badge>

              <Tag
                style={{
                  background: customer.preferred_contact_method === 'phone'
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  boxShadow: customer.preferred_contact_method === 'phone'
                    ? '0 2px 4px rgba(16, 185, 129, 0.3)'
                    : '0 2px 4px rgba(59, 130, 246, 0.3)'
                }}
              >
                {customer.preferred_contact_method}
              </Tag>
            </Space>

            <Space size="small">
              <Button
                type="link"
                icon={<PhoneOutlined />}
                style={{ color: '#059669' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCall(customer);
                }}
              />
              <Button
                type="link"
                icon={<MailOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEmail(customer);
                }}
              />
            </Space>
          </div>
        </div>
      </div>
    </Card>
  );
};

const CustomerList: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'company'>('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const navigate = useNavigate();
  const { user } = useAuth();
  const rolePrefix = getRolePrefix(user?.role);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterAndSortCustomers();
  }, [customers, searchTerm, sortBy]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerApi.getAll();
      setCustomers(response.data);
    } catch (error) {
      message.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortCustomers = () => {
    let filtered = customers.filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.first_name.toLowerCase().includes(searchLower) ||
        customer.last_name.toLowerCase().includes(searchLower) ||
        (customer.email?.toLowerCase().includes(searchLower) || false) ||
        customer.phone.includes(searchTerm) ||
        (customer.company_name && customer.company_name.toLowerCase().includes(searchLower))
      );
    });

    // Sort customers
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = a.company_name || `${a.first_name} ${a.last_name}`;
          const nameB = b.company_name || `${b.first_name} ${b.last_name}`;
          return nameA.localeCompare(nameB);
        case 'company':
          return (a.company_name || '').localeCompare(b.company_name || '');
        case 'date':
          return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
        default:
          return 0;
      }
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  };

  const handleEdit = (customer: Customer) => {
    navigate(`${rolePrefix}/customers/${customer.id}/edit`);
  };

  const handleDelete = async (customer: Customer) => {
    // Add confirmation modal here
    message.info(`Delete functionality for ${customer.first_name} ${customer.last_name} - Coming soon`);
  };

  const handleCall = (customer: Customer) => {
    // Open phone app or show phone number
    if (navigator.userAgent.match(/Mobile/)) {
      window.location.href = `tel:${customer.phone}`;
    } else {
      message.info(`Call ${customer.first_name} at ${customer.phone}`);
    }
  };

  const handleEmail = (customer: Customer) => {
    // Open email client
    window.location.href = `mailto:${customer.email}`;
  };

  const sortMenuItems = [
    { key: 'name', label: 'Sort by Name', onClick: () => setSortBy('name') },
    { key: 'company', label: 'Sort by Company', onClick: () => setSortBy('company') },
    { key: 'date', label: 'Sort by Date Added', onClick: () => setSortBy('date') },
  ];

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return (
      <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', padding: '24px' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: 24 }}>
          <Skeleton.Button active size="large" style={{ width: 200, marginBottom: 12 }} />
          <Skeleton.Input active size="large" block style={{ marginBottom: 16 }} />
        </div>

        {/* Card Skeletons */}
        <Row gutter={[16, 16]} style={{ margin: 0 }}>
          {[...Array(8)].map((_, index) => (
            <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} key={index}>
              <Card style={{ borderRadius: 20, border: '1px solid #e2e8f0' }}>
                <Skeleton avatar={{ size: 64 }} active paragraph={{ rows: 4 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: window.innerWidth >= 768 ? 24 : 16 }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={window.innerWidth >= 768 ? 'middle' : 'small'}>
          <Space direction="vertical" size="small">
            <Title level={window.innerWidth >= 768 ? 2 : 3} style={{ marginBottom: 0 }}>
              Customers ({filteredCustomers.length})
            </Title>
            <Text type="secondary" style={{ fontSize: window.innerWidth >= 768 ? '14px' : '12px' }}>
              Manage your customer database and contact information
            </Text>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size={window.innerWidth >= 768 ? 'large' : 'middle'}
            onClick={() => navigate(`${rolePrefix}/customers/new`)}
          >
            {window.innerWidth >= 768 ? 'Add Customer' : 'Add'}
          </Button>
        </Flex>
        <Flex gap={window.innerWidth >= 768 ? 'middle' : 'small'} wrap="wrap">
          <Search
            placeholder={window.innerWidth >= 768 ? 'Search customers by name, email, phone, or company...' : 'Search customers...'}
            allowClear
            size={window.innerWidth >= 768 ? 'large' : 'middle'}
            style={{ flex: 1, minWidth: window.innerWidth >= 768 ? 300 : 200 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(value) => setSearchTerm(value)}
          />
          <Space>
            <Dropdown menu={{ items: sortMenuItems, selectedKeys: [sortBy] }} trigger={['click']}>
              <Button icon={<SortAscendingOutlined />} size={window.innerWidth >= 768 ? 'large' : 'middle'}>
                {window.innerWidth >= 768 && 'Sort'}
              </Button>
            </Dropdown>
            <Button icon={<FilterOutlined />} size={window.innerWidth >= 768 ? 'large' : 'middle'}>
              {window.innerWidth >= 768 && 'Filter'}
            </Button>
          </Space>
        </Flex>
      </Space>

      {/* Customer Cards */}
      {filteredCustomers.length === 0 ? (
        <Empty
          description="No customers found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '64px 0' }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`${rolePrefix}/customers/new`)}
          >
            Add Your First Customer
          </Button>
        </Empty>
      ) : (
        <>
          <Row
            gutter={[16, 16]}
            style={{
              marginBottom: window.innerWidth >= 768 ? '24px' : '16px',
              marginLeft: 0,
              marginRight: 0,
              width: '100%'
            }}
          >
            {paginatedCustomers.map((customer) => (
              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} key={customer.id}>
                <CustomerCard
                  customer={customer}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCall={handleCall}
                  onEmail={handleEmail}
                  rolePrefix={rolePrefix}
                />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {filteredCustomers.length > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                current={currentPage}
                total={filteredCustomers.length}
                pageSize={pageSize}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} customers`
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerList;